import { type MouseEvent } from 'react';

import type { SearchQuery } from '~/features/advanced-search/types';

import { useActiveToolbarPanelParam } from '~/common/layout/activeToolbarPanelParam';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { Folder } from '~/eds/src/components/Folder/Folder';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { FormatNumberOptions } from '~/eds/src/types/FormatNumberOptions';
import { DataFieldType } from '~/enums/DataFieldType';
import { appendKeywordMatchesQueryParam } from '~/features/document-details-panel/keywordMatchesUrl';
import { intlService } from '~/i18n/IntlService';
import { ColumnSortOrder } from '~/types/Columns';
import { PilotId } from '~/types/PilotId';
import { TableSettings } from '~/types/Table';
// eslint-disable-next-line no-restricted-imports
import Link from '~/ui/components/Link/Link';
import { getUniqueFieldValues } from '~/utils/document.utils';

export const DEFAULT_TABLE_SORT_COLUMN_ORDER: ColumnSortOrder = {
  id: 'name',
  desc: false,
};

export const DEFAULT_TABLE_SORT_COLUMN_PRE_SIGN: ColumnSortOrder = {
  id: 'name_Ticket',
  desc: false,
};

export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  columnOrder: [],
  columnWidths: {},
  pageSize: 20,
  sortBy: DEFAULT_TABLE_SORT_COLUMN_ORDER,
};

export const getValueWithBoolean = (bool: unknown) => {
  if (bool === true || bool === 'Yes') {
    return 'Yes';
  } else if (bool === false || bool === 'No') {
    return 'No';
  } else {
    return undefined;
  }
};

/** Gets the local storage key for Persisted Tables. */
export function getStorageKey(user: { id: PilotId }, context: string) {
  return `evi-u${user.id}-table:${context}`;
}

type FieldLike = { id: string | number; type?: string };
type FieldValueRecord = Record<
  string | number,
  { value: unknown[] | null } | null | undefined
>;

export const renderCellValue = (
  field: FieldLike,
  formatNumber: (
    number: number | bigint | null | undefined,
    options?: FormatNumberOptions,
  ) => string,
  fieldValues: FieldValueRecord = {},
) => {
  const value = fieldValues[field.id]?.value;

  switch (field.type) {
    case DataFieldType.ARRAY_MULTIPLE:
      return getUniqueFieldValues(value ?? []).join(', ');
    case DataFieldType.ARRAY_SINGLE:
      return value;
    case DataFieldType.BOOLEAN: {
      const bool = (value ?? [])[0];
      return getValueWithBoolean(bool);
    }
    case DataFieldType.DATE:
      return (value ?? [])[0];
    case DataFieldType.NUMBER:
      return (value ?? []).map((v) => formatNumber(Number(v))).join(' • ');

    // below cases will fall through
    case DataFieldType.ATTACHMENT:
    case DataFieldType.STRING:
    case DataFieldType.TEXT_AREA:
    default:
      return value;
  }
};

// dashboards documents filter type is EviFieldType
export const renderDashboardsTableCellValue = (
  field: FieldLike,
  fieldValues: FieldValueRecord = {},
) => {
  const value = fieldValues[field.id]?.value;

  switch (field.type) {
    case 'enum_set':
    case 'enum':
      return getUniqueFieldValues(value ?? []).join(', ');
    case 'boolean': {
      const bool = (value ?? [])[0];
      return getValueWithBoolean(bool);
    }
    case 'date':
      return (value ?? [])[0];
    case 'number':
      return (value ?? []).join(' • ');

    // below cases will fall through
    case 'file':
    case 'text':
    case 'text_search':
    default:
      return value;
  }
};

type FolderHandler = {
  path?: Array<{ id: number; name: string }>;
};

export const FolderValue = ({
  handlers = [],
}: {
  handlers?: FolderHandler[];
}) => {
  if (handlers.length > 1) {
    return (
      <Layout align="center" spacing={2}>
        <Icon icon="folder" role="presentation" />
        <Text>
          {intlService.getIntl().formatMessage({
            id: 'table.multipleLocationsLabel',
            defaultMessage: 'Multiple Locations',
            description:
              'Label shown when document is in multiple folder locations',
          })}
        </Text>
      </Layout>
    );
  } else if (handlers[0]?.path) {
    const handlerPath = handlers[0].path;
    const fullFoldersPath = handlerPath.reduce<string[]>(
      (acc, val) => [...acc, val.name],
      [],
    );
    return (
      <Layout styles={{ overflow: 'hidden' }}>
        <Folder
          name={handlerPath[handlerPath.length - 1].name}
          link={{
            pathname: `/documents/${handlerPath[handlerPath.length - 1].id}`,
          }}
          path={fullFoldersPath.join('/')}
        />
      </Layout>
    );
  } else {
    return null;
  }
};

type HandlerNameHandler = {
  id: number;
  document_name?: string;
  name?: string;
  file_type: string;
};

interface HandlerNameProps {
  handlers?: HandlerNameHandler[] | null;
  openTarget?: string;
  onClick?: (event?: MouseEvent) => void;
  onNavigate?: (handlerId: number) => void;
  /** Current search query, threaded through to `appendKeywordMatchesQueryParam`. */
  query?: SearchQuery;
}

export const HandlerName = ({
  handlers = [],
  openTarget,
  onClick,
  onNavigate,
  query,
}: HandlerNameProps) => {
  const { activePanelId } = useActiveToolbarPanelParam();

  if (handlers?.length) {
    const firstHandler = handlers[0];
    const fileName = `${firstHandler.document_name ?? firstHandler.name}${
      firstHandler.file_type
    }`;

    const handleOnClick = (event: MouseEvent) => {
      event.stopPropagation();
      onClick?.(event);
    };

    const handleNavigate = (e: MouseEvent) => {
      e.stopPropagation();
      onNavigate?.(handlers[0].id);
    };

    return (
      <Layout minW={0} w="100%">
        {handlers.length > 1 ? (
          <Link onClick={onClick ? handleOnClick : undefined}>
            <Filename name={fileName} includeExtension={false} />
          </Link>
        ) : (
          <Link
            target={openTarget}
            to={appendKeywordMatchesQueryParam(
              `/document/${handlers[0].id}`,
              query,
              { activePanelId },
            )}
            onClick={handleNavigate}
          >
            <Filename name={fileName} includeExtension={false} />
          </Link>
        )}
      </Layout>
    );
  } else {
    return <Filename name="Deleted" includeExtension={false} />;
  }
};
